import { api } from '@/lib/api';
import { cn } from '@/lib/cn';
import type { DirectoryAttribute, SharePrincipalType } from '@innermate/shared';
import { useQuery } from '@tanstack/react-query';
import { Search, Users } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { UserDisplay } from './UserDisplay';

/** 검색 후보 — 사용자 / 개인그룹 / 역할 공용. */
export interface PickerCandidate {
  kind: SharePrincipalType;
  id: string;
  name: string;
  subtitle: string | null;
  /** 사용자일 때만. */
  attributes?: DirectoryAttribute[];
}

interface Props {
  /** 이미 선택된 후보 key (`${kind}:${id}`) — 결과에서 제외. */
  excludeKeys: Set<string>;
  onSelect: (c: PickerCandidate) => void;
  placeholder?: string;
  /** true 면 디렉터리(사용자) 검색 포함. */
  includeUsers?: boolean;
  /** 로컬 후보(개인그룹/역할 등)를 검색어로 필터해서 함께 노출. */
  localCandidates?: PickerCandidate[];
  autoFocus?: boolean;
}

export function candidateKey(kind: SharePrincipalType, id: string): string {
  return `${kind}:${id}`;
}

/**
 * 공유/그룹 화면 공용 검색 입력 + 드롭다운.
 * - includeUsers: 디렉터리(사용자) 부분일치 검색(디바운스 250ms).
 * - localCandidates: 개인그룹/역할 등 클라이언트 보유 후보를 검색어로 필터해 상단에 노출.
 * 동명이인 구분을 위해 사용자 행에는 노출 허용 속성 칩을 함께 표시한다.
 */
export function UserSearchPicker({
  excludeKeys,
  onSelect,
  placeholder = '이름 또는 이메일로 검색',
  includeUsers = true,
  localCandidates = [],
  autoFocus,
}: Props) {
  const [draft, setDraft] = useState('');
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (autoFocus) inputRef.current?.focus();
  }, [autoFocus]);

  useEffect(() => {
    const h = setTimeout(() => setQ(draft.trim()), 250);
    return () => clearTimeout(h);
  }, [draft]);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const usersQuery = useQuery({
    queryKey: ['directory', 'search', q],
    queryFn: () => api.directorySearch(q),
    enabled: includeUsers && q.length > 0,
    staleTime: 10_000,
  });

  const localMatches = useMemo(() => {
    const needle = q.toLowerCase();
    if (!needle) return localCandidates;
    return localCandidates.filter((c) => c.name.toLowerCase().includes(needle));
  }, [localCandidates, q]);

  const userCandidates: PickerCandidate[] = useMemo(
    () =>
      (usersQuery.data?.users ?? []).map((u) => ({
        kind: 'user' as const,
        id: u.id,
        name: u.displayName,
        subtitle: u.email,
        attributes: u.attributes,
      })),
    [usersQuery.data],
  );

  const results = useMemo(
    () =>
      [...localMatches, ...userCandidates].filter(
        (c) => !excludeKeys.has(candidateKey(c.kind, c.id)),
      ),
    [localMatches, userCandidates, excludeKeys],
  );

  const showDropdown = open && (q.length > 0 || localMatches.length > 0);

  return (
    <div ref={rootRef} className="relative">
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 size-4 text-foreground-muted" />
        <input
          ref={inputRef}
          type="text"
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          placeholder={placeholder}
          className="h-10 w-full rounded-lg border border-border bg-background pl-9 pr-3 text-sm outline-none focus:border-accent"
        />
      </div>

      {showDropdown ? (
        <div className="absolute z-50 mt-1 max-h-80 w-full overflow-y-auto rounded-lg border border-border bg-surface shadow-lg py-1">
          {includeUsers && usersQuery.isLoading && q.length > 0 ? (
            <div className="px-3 py-2 text-xs text-foreground-muted">검색 중…</div>
          ) : results.length === 0 ? (
            <div className="px-3 py-3 text-xs text-foreground-muted">
              {q.length > 0 ? '일치하는 대상이 없습니다.' : '검색어를 입력하세요.'}
            </div>
          ) : (
            results.map((c) => (
              <button
                key={candidateKey(c.kind, c.id)}
                type="button"
                onClick={() => {
                  onSelect(c);
                  setDraft('');
                  setQ('');
                  setOpen(false);
                }}
                className="flex w-full items-center gap-2.5 px-3 py-1.5 text-left hover:bg-surface-muted"
              >
                {c.kind === 'user' ? (
                  <UserDisplay
                    displayName={c.name}
                    email={c.subtitle ?? ''}
                    attributes={c.attributes}
                    size="sm"
                    className="flex-1"
                  />
                ) : (
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <span
                      className={cn(
                        'size-7 shrink-0 rounded-full grid place-items-center',
                        c.kind === 'user_group'
                          ? 'bg-accent/15 text-accent'
                          : 'bg-surface-muted text-foreground-muted',
                      )}
                    >
                      <Users className="size-3.5" />
                    </span>
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">{c.name}</div>
                      <div className="text-xs text-foreground-muted truncate">
                        {c.kind === 'user_group' ? '내 그룹' : '역할'}
                        {c.subtitle ? ` · ${c.subtitle}` : ''}
                      </div>
                    </div>
                  </div>
                )}
              </button>
            ))
          )}
        </div>
      ) : null}
    </div>
  );
}
